import React, { useState, useCallback } from 'react'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Box,
  Stack,
  Alert,
  Stepper,
  Step,
  StepLabel,
  IconButton
} from '@mui/material'
import { Close, CheckCircleOutline, QrCode2, PhoneAndroid, ContentCopy, Check } from '@mui/icons-material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { setupMFA, verifyMFA } from 'service/mfa'
import { accent } from 'mainTheme'

const STEPS = { SCAN: 0, VERIFY: 1, SUCCESS: 2 }
const STEP_LABELS = ['Scan QR Code', 'Verify']

const MFASetup = ({ open, onClose, onEnabled }) => {
  const [step, setStep] = useState(STEPS.SCAN)
  const [code, setCode] = useState('')
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback((text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [])
  const queryClient = useQueryClient()

  const setupMutation = useMutation({ mutationFn: setupMFA })

  const verifyMutation = useMutation({
    mutationFn: () => verifyMFA(code.trim()),
    onSuccess: () => {
      setStep(STEPS.SUCCESS)
      onEnabled?.()
      queryClient.invalidateQueries(['mfaStatus'])
    }
  })

  const handleOpen = () => {
    setStep(STEPS.SCAN)
    setCode('')
    setupMutation.reset()
    verifyMutation.reset()
    setupMutation.mutate()
  }

  const handleVerify = () => {
    if (!code.trim()) return
    verifyMutation.mutate()
  }

  const handleClose = () => {
    setStep(STEPS.SCAN)
    setCode('')
    setupMutation.reset()
    verifyMutation.reset()
    onClose()
  }

  const qrCode = setupMutation.data?.qrCode
  const secretCode = setupMutation.data?.secretCode
  const error = setupMutation.error?.response?.data?.message ?? verifyMutation.error?.response?.data?.message

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => reason !== 'backdropClick' && handleClose()}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      TransitionProps={{ onEntered: handleOpen }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '1px solid rgba(255,255,255,0.08)',
          bgcolor: 'background.paper'
        }
      }}>
      {/* Header */}
      <DialogTitle sx={{ pb: 0, pt: 2.5, px: 3, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: `${accent}22`,
                border: `1px solid ${accent}44`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: accent,
                flexShrink: 0
              }}>
              <PhoneAndroid fontSize="small" />
            </Box>
            <Typography variant="h5" sx={{ fontSize: '16px', fontWeight: 500 }}>
              Set up Authenticator App
            </Typography>
          </Stack>
          <IconButton size="small" onClick={handleClose} sx={{ color: 'rgba(255,255,255,0.4)', mr: -0.5 }}>
            <Close fontSize="small" color="secondary" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 0 }}>
        {/* Stepper — only shown during scan/verify steps */}
        {step !== STEPS.SUCCESS && (
          <Stepper
            activeStep={step}
            sx={{
              mb: 3,
              '& .MuiStepLabel-label': { fontSize: '12px', color: 'rgba(255,255,255,0.4)' },
              '& .MuiStepLabel-label.Mui-active': { color: 'white' },
              '& .MuiStepLabel-label.Mui-completed': { color: accent },
              '& .MuiStepIcon-root': { color: 'rgba(255,255,255,0.15)' },
              '& .MuiStepIcon-root.Mui-active': { color: accent },
              '& .MuiStepIcon-root.Mui-completed': { color: accent },
              '& .MuiStepConnector-line': { borderColor: 'rgba(255,255,255,0.1)' }
            }}>
            {STEP_LABELS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {/* SCAN step — loading */}
        {setupMutation.isLoading && step === STEPS.SCAN && (
          <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={2}>
            <CircularProgress size={36} sx={{ color: accent }} />
            <Typography variant="body2" sx={{ opacity: 0.5 }}>
              Generating QR code…
            </Typography>
          </Box>
        )}

        {/* SCAN step — loaded */}
        {!setupMutation.isLoading && step === STEPS.SCAN && qrCode && (
          <Stack spacing={2.5}>
            <Typography variant="body2" sx={{ opacity: 0.7, lineHeight: 1.6 }}>
              Open your authenticator app and scan the code below. We recommend{' '}
              <Box component="span" sx={{ color: accent }}>
                Google Authenticator
              </Box>{' '}
              or{' '}
              <Box component="span" sx={{ color: accent }}>
                Microsoft Authenticator
              </Box>
              .
            </Typography>

            {/* QR code */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                p: 2,
                bgcolor: 'white',
                borderRadius: 2,
                mx: 'auto',
                width: 'fit-content'
              }}>
              <img src={qrCode} alt="MFA QR Code" width={176} height={176} style={{ display: 'block' }} />
            </Box>

            {/* Manual key */}
            <Box
              sx={{
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 2,
                p: 1.5,
                bgcolor: 'rgba(255,255,255,0.03)'
              }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <QrCode2 sx={{ fontSize: 14, color: 'rgba(255,255,255,0.35)' }} />
                  <Typography
                    variant="caption"
                    sx={{ opacity: 0.4, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Can&apos;t scan? Enter manually
                  </Typography>
                </Stack>
                <IconButton
                  size="small"
                  onClick={() => handleCopy(secretCode)}
                  sx={{
                    color: copied ? '#3fb950' : 'rgba(255,255,255,0.35)',
                    transition: 'color 0.2s',
                    '&:hover': { color: copied ? '#3fb950' : accent },
                    mr: -0.5
                  }}>
                  {copied ? <Check sx={{ fontSize: 15 }} /> : <ContentCopy sx={{ fontSize: 15 }} />}
                </IconButton>
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  color: accent,
                  fontSize: '13px',
                  letterSpacing: '0.05em'
                }}>
                {secretCode}
              </Typography>
            </Box>
          </Stack>
        )}

        {/* VERIFY step */}
        {step === STEPS.VERIFY && (
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ opacity: 0.7, lineHeight: 1.6 }}>
              Enter the 6-digit code shown in your authenticator app to confirm setup.
            </Typography>
            <TextField
              label="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              fullWidth
              autoFocus
              sx={{
                '& input': {
                  fontSize: '22px',
                  letterSpacing: '0.35em',
                  textAlign: 'center',
                  py: 1.5
                }
              }}
            />
          </Stack>
        )}

        {/* SUCCESS step */}
        {step === STEPS.SUCCESS && (
          <Stack alignItems="center" spacing={2} py={2}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'rgba(46,160,67,0.12)',
                border: '1px solid rgba(46,160,67,0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              <CheckCircleOutline sx={{ fontSize: 32, color: '#3fb950' }} />
            </Box>
            <Box textAlign="center">
              <Typography variant="h5" sx={{ mb: 0.75 }}>
                MFA enabled
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.55, lineHeight: 1.6 }}>
                Your account is now protected with two-factor authentication. You&apos;ll be prompted for a code on your
                next sign-in.
              </Typography>
            </Box>
          </Stack>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{
              mt: 2,
              borderRadius: 2,
              bgcolor: 'rgba(236,5,33,0.1)',
              border: '1px solid rgba(236,5,33,0.3)',
              color: '#ff6b6b',
              '& .MuiAlert-icon': { color: '#ff6b6b' }
            }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
        {step === STEPS.SUCCESS ? (
          <Button onClick={handleClose} variant="contained" fullWidth>
            Done
          </Button>
        ) : step === STEPS.SCAN ? (
          <>
            <Button onClick={handleClose} sx={{ flex: 1 }}>
              Cancel
            </Button>
            <Button
              onClick={() => setStep(STEPS.VERIFY)}
              disabled={!qrCode || setupMutation.isLoading}
              variant="contained"
              sx={{ flex: 2 }}>
              Next
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setStep(STEPS.SCAN)} sx={{ flex: 1 }}>
              Back
            </Button>
            <Button
              onClick={handleVerify}
              disabled={verifyMutation.isLoading || code.length < 6}
              variant="contained"
              sx={{ flex: 2 }}>
              {verifyMutation.isLoading ? <CircularProgress size={18} /> : 'Verify'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default MFASetup
